// src/pages/healthcare/consultation/consultationComponents/ConsultRightPanel.tsx
import React from "react";
import { Space, Badge, Segmented } from "antd";
import { BulbOutlined, FileTextOutlined, ApartmentOutlined, CommentOutlined, MessageOutlined } from "@ant-design/icons";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { PatientNote } from "../../../../types/healthcare/PatientNotes";
import { Appointment } from "../../../../types/appointments/AppointmentTypes";
import { PatientTreatmentPlan } from "../../../../types/healthcare/TreatmentPlanTypes";
import { TeamMember } from "../../../../types/admin/TeamTypes";
import { ConsultChatMessage } from "../../../../types/appointments/ConsultChatTypes";
import { Suggestion } from "../../../../utils/healthcare/SugestionUtils";

import SuggestionPanel from "./SuggestionPanel";
import Notes from "../../patient/modalComponents/Notes";
import ConsultTreatmentNotePanel from "./ConsultTreatmentNotePanel";
import TranscriptList, { TranscriptListProps } from "./Transcript";
import ConsultChatPanel from "../../../../components/consult/ConsultChatPanel";

export type RightPanelMode = "suggestions" | "notes" | "plan_notes" | "transcripts" | "chat";

interface RightPanelSuggestions {
	items: Suggestion[];
	aiActive: boolean;
	toggleAI: () => void;
	updateSuggestion: (suggestionId: string, updates: Partial<Suggestion>) => void;
}

interface RightPanelNotes {
	items: PatientNote[];
	setItems: React.Dispatch<React.SetStateAction<PatientNote[]>>;
	onSave: () => void;
}

interface RightPanelChat {
	messages: ConsultChatMessage[];
	onSend: (content: string) => boolean | void;
	onAttach?: (file: File) => Promise<boolean | void> | boolean | void;
	unreadCount: number;
	error?: string | null;
	onDismissError?: () => void;
	emptyText?: string;
}

interface ConsultRightPanelProps {
	mode: RightPanelMode;
	onModeChange: (mode: RightPanelMode) => void;
	activePlan: PatientTreatmentPlan | null;
	consultSessionId: number | null;
	patient: Patient;
	setPatient: React.Dispatch<React.SetStateAction<Patient>>;
	practitioner: TeamMember;
	appointment: Appointment;
	isDisabled?: boolean;
	suggestions: RightPanelSuggestions;
	notes: RightPanelNotes;
	transcript: TranscriptListProps;
	chat?: RightPanelChat;
}

interface RightPanelToggleProps {
	mode: RightPanelMode;
	onModeChange: (mode: RightPanelMode) => void;
	pendingSuggestionCount: number;
	hasSession: boolean;
	activePlan: PatientTreatmentPlan | null;
	showChat: boolean;
	chatUnreadCount: number;
}

// Module scope — declaring this inside the panel's render body remounted the Segmented every render.
const RightPanelToggle: React.FC<RightPanelToggleProps> = ({ mode, onModeChange, pendingSuggestionCount, hasSession, activePlan, showChat, chatUnreadCount }) => (
	<div style={{ marginBottom: 8 }}>
		<Segmented
			size="small"
			block
			value={mode}
			onChange={(v) => onModeChange(v as RightPanelMode)}
			options={[
				{
					value: "suggestions",
					icon: <BulbOutlined />,
					label: (
						<Space size={4}>
							<span style={{ fontSize: 11 }}>AI Suggestions</span>
							{pendingSuggestionCount > 0 && <Badge count={pendingSuggestionCount} size="small" style={{ fontSize: 9 }} />}
						</Space>
					),
				},
				{
					value: "notes",
					icon: <FileTextOutlined />,
					label: (
						<Space size={4}>
							<span style={{ fontSize: 11 }}>Notes</span>
							{hasSession && <Badge dot status="success" style={{ fontSize: 9 }} />}
						</Space>
					),
				},
				...(activePlan
					? [
						{
							value: "plan_notes" as const,
							icon: <ApartmentOutlined />,
							label: (
								<Space size={4}>
									<span style={{ fontSize: 11 }}>Plan Notes</span>
									<Badge dot status="processing" style={{ fontSize: 9 }} />
								</Space>
							),
						},
					]
					: []),
				{
					value: "transcripts",
					icon: <CommentOutlined />,
					label: (
						<Space size={4}>
							<span style={{ fontSize: 11 }}>Transcripts</span>
							{hasSession && <Badge dot status="success" style={{ fontSize: 9 }} />}
						</Space>
					),
				},
				...(showChat
					? [
						{
							value: "chat" as const,
							// Not CommentOutlined — that is already the Transcripts icon on this same control.
							icon: <MessageOutlined />,
							label: (
								<Space size={4}>
									<span style={{ fontSize: 11 }}>Chat</span>
									{chatUnreadCount > 0 && <Badge count={chatUnreadCount} size="small" style={{ fontSize: 9 }} />}
								</Space>
							),
						},
					]
					: []),
			]}
			style={{ fontSize: 11 }}
		/>
	</div>
);

const ConsultRightPanel: React.FC<ConsultRightPanelProps> = ({ mode, onModeChange, activePlan, consultSessionId, patient, setPatient, practitioner, appointment, isDisabled, suggestions, notes, transcript, chat }) => {
	const pendingSuggestionCount = suggestions.items.filter((s) => s.status === "pending").length;
	const hasSession = !!consultSessionId;

	return (
		<div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", padding: 8 }} data-testid="consultation-right-panel">
			<RightPanelToggle mode={mode} onModeChange={onModeChange} pendingSuggestionCount={pendingSuggestionCount} hasSession={hasSession} activePlan={activePlan} showChat={!!chat} chatUnreadCount={chat?.unreadCount ?? 0} />

			<div style={{ flex: 1, overflow: "auto" }}>
				{mode === "suggestions" && (
					<SuggestionPanel
						aiActive={suggestions.aiActive}
						patient={patient}
						setPatient={setPatient}
						suggestions={suggestions.items}
						updateSuggestion={suggestions.updateSuggestion}
						toggleAI={suggestions.toggleAI}
						isDisabled={isDisabled}
						transcript={transcript.transcripts}
						currentUser={practitioner}
					/>
				)}
				{mode === "notes" && <Notes patient={patient} notes={notes.items} setNotes={notes.setItems} handleAutoSave={notes.onSave} />}

				{mode === "plan_notes" && <ConsultTreatmentNotePanel plan={activePlan} appointment={appointment} patient={patient} />}

				{mode === "transcripts" && <TranscriptList {...transcript} />}

				{mode === "chat" && chat && (
					<ConsultChatPanel messages={chat.messages} currentRole="practitioner" onSend={chat.onSend} onAttach={chat.onAttach} disabled={isDisabled} emptyText={chat.emptyText} error={chat.error} onDismissError={chat.onDismissError} />
				)}
			</div>
		</div>
	);
};

export default ConsultRightPanel;
